import React, { useState, useEffect, useCallback } from 'react';
import {
  FileText, Plus, Trash2, AlertTriangle, CheckCircle2, Send,
  Mail, MessageSquare, Pencil,
} from 'lucide-react';
import apiClient from '../../utils/apiClient';
import { formatDateTime } from '../../utils/datetime';
import CustomEmailTemplateModal from '../../components/CustomEmailTemplateModal';

/**
 * CustomTemplatesTab — operator-authored templates (email + text).
 *
 * These are kind="custom" rows in template_overrides. They do NOT fire from
 * automation rules - operators reach them through Communication > Custom
 * Templates to broadcast announcements / rate-change notices / etc.
 *
 * Each row carries a `channel`: "email" (subject + HTML body, sent via the
 * bulk-email composer in the modal) or "sms" (plain-text body; the actual
 * send happens from the per-customer Text tab). Legacy rows with no channel
 * read as email. Two create buttons author each kind; a per-row badge shows
 * which channel a template targets.
 */

// ============================================================================
// CustomTemplatesTab
// ============================================================================
const CustomTemplatesTab = ({ facilityId, facility }) => {
  const timeZone = facility?.timezone || undefined;
  const [templates, setTemplates] = useState([]);
  const [usageByKey, setUsageByKey] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);

  // Unified modal state: null template = "new", non-null = "edit existing".
  // newChannel drives which kind of template a "New ..." button creates;
  // for edits the channel comes from the row itself.
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [newChannel, setNewChannel] = useState('email');
  const [modalOpen, setModalOpen] = useState(false);

  const load = useCallback(async () => {
    if (!facilityId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get(
        `/api/facilities/${facilityId}/templates/`,
        { params: { kind: 'custom' } },
      );
      const rows = Array.isArray(res.data) ? res.data : [];
      setTemplates(rows);

      // Best-effort usage counts. Endpoint may not be wired; swallow errors.
      try {
        const usage = await apiClient.get(
          `/api/facilities/${facilityId}/notifications/usage`,
          { params: { kind: 'custom' } },
        );
        const map = {};
        (usage.data || []).forEach((u) => { map[u.template_key] = u.sent_count || 0; });
        setUsageByKey(map);
      } catch (e) {
        setUsageByKey({});
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load custom templates.');
    } finally {
      setLoading(false);
    }
  }, [facilityId]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (tpl) => {
    if (!window.confirm(`Delete custom template "${tpl.name}"? This cannot be undone.`)) return;
    try {
      await apiClient.delete(`/api/facilities/${facilityId}/templates/${tpl.template_key}`);
      setInfo('Template deleted.');
      setTimeout(() => setInfo(null), 3000);
      load();
    } catch (e) {
      setError(e.response?.data?.detail || 'Delete failed.');
    }
  };

  const openNew = (channel) => {
    setEditingTemplate(null);
    setNewChannel(channel);
    setModalOpen(true);
  };

  const openEditSend = (row) => {
    setEditingTemplate(row);
    // For an existing row the modal reads channel off the template itself.
    setNewChannel(row.channel || 'email');
    setModalOpen(true);
  };

  if (loading) return <p className="text-[13px] text-slate-500">Loading custom templates...</p>;

  return (
    <div>
      <p className="text-[13px] text-slate-500 mb-4 max-w-3xl">
        Custom templates are operator-authored, for both email and text. They
        don&apos;t fire from automation rules - use them for one-off announcements
        or bulk messages. Email templates edit and send from one screen; text
        templates are authored here and sent from a tenant&apos;s Text tab.
      </p>

      {error && (
        <div className="mb-3 rounded-md bg-rose-50 border border-rose-200 px-3 py-2 text-[12px] text-rose-700 flex items-center gap-2">
          <AlertTriangle className="h-3.5 w-3.5" /> {error}
        </div>
      )}
      {info && (
        <div className="mb-3 rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2 text-[12px] text-emerald-700 flex items-center gap-2">
          <CheckCircle2 className="h-3.5 w-3.5" /> {info}
        </div>
      )}

      <div className="flex justify-between items-center mb-3">
        <p className="text-[13px] text-slate-500">{templates.length} template{templates.length === 1 ? '' : 's'}</p>
        <div className="flex items-center gap-2">
          <button
            onClick={() => openNew('email')}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-[12px] font-medium"
            type="button"
          >
            <Mail className="h-3.5 w-3.5" /> New Email Template
          </button>
          <button
            onClick={() => openNew('sms')}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-[12px] font-medium"
            type="button"
          >
            <MessageSquare className="h-3.5 w-3.5" /> New Text Template
          </button>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200/80 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left font-medium px-4 py-2.5">Name</th>
                <th className="text-left font-medium px-4 py-2.5 w-24">Channel</th>
                <th className="text-left font-medium px-4 py-2.5">Last edited</th>
                <th className="text-left font-medium px-4 py-2.5">Used by</th>
                <th className="text-left font-medium px-4 py-2.5 w-28">Status</th>
                <th className="text-right font-medium px-4 py-2.5 w-48">Actions</th>
              </tr>
            </thead>
            <tbody>
              {templates.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-6 text-center text-slate-400 text-[12px]">
                    No custom templates yet. Click &ldquo;New Email Template&rdquo; or
                    {' '}&ldquo;New Text Template&rdquo; to create one.
                  </td>
                </tr>
              )}
              {templates.map((t) => {
                const used = usageByKey[t.template_key] || 0;
                const isSms = (t.channel || 'email') === 'sms';
                return (
                  <tr key={t.template_key} className="border-t border-slate-100 align-top">
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        {isSms ? (
                          <MessageSquare className="h-3.5 w-3.5 text-slate-400" />
                        ) : (
                          <FileText className="h-3.5 w-3.5 text-slate-400" />
                        )}
                        <div>
                          <div className="font-medium text-slate-900">{t.name || t.template_key}</div>
                          <div className="text-[11px] text-slate-400 font-mono">{t.template_key}</div>
                          {t.description && (
                            <div className="text-[11px] text-slate-500 mt-0.5">{t.description}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-2.5">
                      {isSms ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-violet-50 text-violet-700 ring-1 ring-violet-200">
                          <MessageSquare className="h-3 w-3" /> Text
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-blue-50 text-blue-700 ring-1 ring-blue-200">
                          <Mail className="h-3 w-3" /> Email
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-[12px] text-slate-600">{formatDateTime(t.updated_at, timeZone) || '-'}</td>
                    <td className="px-4 py-2.5 text-[12px] text-slate-600">
                      {used > 0 ? `${used} send${used === 1 ? '' : 's'}` : <span className="text-slate-400">-</span>}
                    </td>
                    <td className="px-4 py-2.5">
                      {t.enabled !== false ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200">
                          Enabled
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-slate-50 text-slate-600 ring-1 ring-slate-200">
                          Disabled
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <button
                        onClick={() => openEditSend(t)}
                        className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium text-white bg-blue-600 hover:bg-blue-700 rounded"
                        type="button"
                      >
                        {isSms ? (
                          <><Pencil className="h-3 w-3" /> Edit</>
                        ) : (
                          <><Send className="h-3 w-3" /> Edit &amp; Send</>
                        )}
                      </button>
                      <button
                        onClick={() => handleDelete(t)}
                        className="inline-flex items-center gap-1 px-2 py-1 text-[11px] text-rose-600 hover:bg-rose-50 rounded ml-1"
                        type="button"
                        title="Delete this template"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {modalOpen && (
        <CustomEmailTemplateModal
          template={editingTemplate}
          channel={newChannel}
          facility={facility}
          facilityId={facilityId}
          onClose={() => setModalOpen(false)}
          onSaved={(saved) => {
            // Keep modal open so the operator can also send. Just refresh
            // the underlying list so the new row appears AND newly-created
            // templates can be reopened later.
            setInfo('Template saved.');
            setTimeout(() => setInfo(null), 3000);
            // If we just created a brand-new template, swap the modal to
            // "edit" mode by seeding editingTemplate with the saved doc.
            if (!editingTemplate && saved) {
              setEditingTemplate(saved);
            } else if (saved) {
              setEditingTemplate(saved);
            }
            load();
          }}
          onSent={() => {
            setInfo('Email sent.');
            setTimeout(() => setInfo(null), 3000);
            load();
          }}
        />
      )}
    </div>
  );
};

export default CustomTemplatesTab;
